/**
 * The sentences for sign-in and sign-up (#371), keyed by the contract's error
 * codes and not by anything the server wrote.
 *
 * The server's `message` is never rendered (the envelope's own description
 * says why: the UI owes the person a sentence in its own words). These are
 * those words. Two of them are deliberately vague, and the vagueness is the
 * server's design reaching the screen:
 *
 * - **`invalid_credentials`** is the one answer to every failed sign-in - no
 *   such username, wrong password, a disabled account, a username that is
 *   locked out for a quarter of an hour. If this page could say which, so
 *   could anyone typing into it, and they would learn which names exist.
 * - **`signup_refused`** is the one answer to an invite code that is unknown,
 *   expired or already used, for the same reason.
 *
 * No digit appears in a sentence that is not a rule the person can act on: the
 * password-length floor is a requirement, not a measurement, and nothing here
 * is a proportion (`no-percentage.test.tsx` sweeps every route).
 */

import type { ErrorCode } from '@davinci/contracts';
import { defineMessages } from './messages.js';

/** Every sentence on the sign-in and sign-up pages; the rules are stated once so the hint and the check cannot disagree. */
export const authMessages = defineMessages({
  en: {
    invalidCredentials: 'That username and password do not match an account. Check both and try again.',
    signInRateLimited: 'There have been a lot of sign-in attempts just now. Wait a minute, then try again.',
    signInValidation: 'Enter your username and your password.',
    signupRefused:
      'That invite code cannot be used. It may have expired or already been used. Ask for a new one.',
    usernameTaken:
      'That username is already taken. Choose a different one — your invite code was not used up.',
    signUpRateLimited: 'There have been a lot of sign-up attempts just now. Wait a minute, then try again.',
    signUpValidation: 'Some of what you entered is not allowed. Check the fields below and try again.',
    usernameRule:
      'Letters, digits, dots, underscores and hyphens. At least three characters. Capital letters are treated as lower case.',
    passwordRule:
      'At least twelve characters. There are no other rules — a few unrelated words make a strong one.',
  },
  he: {
    invalidCredentials: 'שם המשתמש והסיסמה לא תואמים לאף חשבון. כדאי לבדוק את שניהם ולנסות שוב.',
    signInRateLimited: 'היו הרבה ניסיונות התחברות ברגעים האחרונים. כדאי להמתין דקה ואז לנסות שוב.',
    signInValidation: 'יש להזין את שם המשתמש ואת הסיסמה.',
    signupRefused:
      'אי אפשר להשתמש בקוד ההזמנה הזה. ייתכן שהוא פג תוקף או שכבר נעשה בו שימוש. כדאי לבקש קוד חדש.',
    usernameTaken: 'שם המשתמש הזה כבר תפוס. כדאי לבחור שם אחר — קוד ההזמנה שלך לא נוצל.',
    signUpRateLimited: 'היו הרבה ניסיונות הרשמה ברגעים האחרונים. כדאי להמתין דקה ואז לנסות שוב.',
    signUpValidation: 'חלק ממה שהוזן אינו מותר. כדאי לבדוק את השדות שלמטה ולנסות שוב.',
    usernameRule:
      'אותיות באנגלית, ספרות, נקודות, קווים תחתונים ומקפים. לפחות שלושה תווים. אותיות גדולות נחשבות לקטנות.',
    passwordRule: 'לפחות שתים-עשרה תווים. אין חוקים נוספים — כמה מילים לא קשורות יוצרות סיסמה חזקה.',
  },
});

type AuthMessages = (typeof authMessages)['en'];

/** What a sign-in failure means to the person, by the server's code. */
export function signInFailureSentence(code: ErrorCode | undefined, t: AuthMessages): string | undefined {
  switch (code) {
    case 'invalid_credentials':
      return t.invalidCredentials;
    case 'rate_limited':
      return t.signInRateLimited;
    case 'validation_failed':
      return t.signInValidation;
    default:
      return undefined;
  }
}

/** What a sign-up failure means to the person, by the server's code. */
export function signUpFailureSentence(code: ErrorCode | undefined, t: AuthMessages): string | undefined {
  switch (code) {
    case 'signup_refused':
      return t.signupRefused;
    case 'username_taken':
      return t.usernameTaken;
    case 'rate_limited':
      return t.signUpRateLimited;
    case 'validation_failed':
      return t.signUpValidation;
    default:
      return undefined;
  }
}

export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 256;
export const USERNAME_PATTERN = /^[A-Za-z0-9._-]{3,32}$/;
